import { useAlarmAudio } from "contexts/AlarmAudioContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { Config } from "services/configuration/eatSmartConfig";
import { type Maybe, TransactionState, TransactionType } from "types";
import { howlerState, networkTrace, shouldTraceNetworkDebug } from "utils/debug/networkTrace";
import { useCountdown } from "./timers";

export const useOpenDoorAlarm = (orderType?: Maybe<TransactionType>, transactionState?: Maybe<TransactionState>) => {
	const { alarms } = Config;
	const { displayWarningTimeout, firstAlarm, secondAlarm, thirdAlarm, totalAlarmTimeout, refillWarningTimeout } =
		alarms;
	const warningDelay = totalAlarmTimeout;
	const playSoundParamFromUrl = new URLSearchParams(window.location.search).get("playSound");

	const isRefill = orderType === TransactionType.Refill;
	// When order type is refill, we delay the initial warning by 300s (5 minutes)
	// we have to set the delay to 400 so the first warning is triggered when countdown reaches 100s
	const initialTimer = isRefill ? refillWarningTimeout : warningDelay;
	const { countdown, resetCountdown, startCountdown } = useCountdown(initialTimer);
	const [displayOpenedDoorWarning, setDisplayOpenedDoorWarning] = useState<boolean>(false);

	// The three alarm players are loaded ONCE at app start by AlarmAudioProvider and reused across every
	// order — the kiosk runs with the HTTP cache off, so loading them per order (the old behaviour) re-
	// downloaded ~7 MB of MP3s on every OrderStep3 mount. This hook only drives playback + the countdown;
	// it never loads or unloads. `alarm1`/`alarm2`/`alarm3` keep the full player so the TEMP diagnostic
	// below can read `.error`; the destructured play/stop names are unchanged.
	const { alarm1, alarm2, alarm3 } = useAlarmAudio();
	const { play: alarm1Play, stop: alarm1Stop } = alarm1;
	const { play: alarm2Play, stop: alarm2Stop } = alarm2;
	const { play: alarm3Play, stop: alarm3Stop } = alarm3;

	const resetAlarmState = useCallback(() => {
		alarm1Stop();
		alarm2Stop();
		alarm3Stop();
		setDisplayOpenedDoorWarning(false);
	}, [alarm1Stop, alarm2Stop, alarm3Stop]);

	const handleResetCountdown = () => {
		resetCountdown(true);
		setDisplayOpenedDoorWarning(false);
		resetAlarmState();
	};

	// Start the countdown when an order reaches step 3, and on unmount silence the alarms and stop the
	// countdown. Loading the sounds is NOT done here anymore — they are preloaded once by
	// AlarmAudioProvider (see its rationale) and merely played/stopped from this hook.
	useEffect(() => {
		startCountdown(true);
		return () => {
			resetAlarmState();
			startCountdown(false);
		};
	}, [resetAlarmState, startCountdown]);

	// The open-door warning + alarm only apply while the transaction is ACTIVE (the door is open
	// mid-transaction). The instant it leaves Active — StockCheck or Finished — fully stop: silence the
	// alarms, hide the warning, AND stop the countdown. Stopping (not restarting) is the key difference
	// from the manual "I'm still here" confirm: a restarted countdown would re-reach the warning
	// threshold and re-trigger while the transaction lingers (e.g. a long refill sitting at StockCheck),
	// which is why the refill warning would not go away on its own. A transaction that disappears
	// entirely (state undefined) is intentionally NOT handled here — OrderStep3 unmounts with it and the
	// mount effect's cleanup stops everything.
	useEffect(() => {
		if (transactionState != null && transactionState !== TransactionState.Active) {
			resetAlarmState();
			startCountdown(false);
		}
	}, [transactionState, resetAlarmState, startCountdown]);

	useEffect(() => {
		if (countdown === displayWarningTimeout) {
			setDisplayOpenedDoorWarning(true);
		} else if (playSoundParamFromUrl && playSoundParamFromUrl === "true") {
			if (countdown === firstAlarm.timeout) {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[audio] play() alarm1 @${countdown}; ${howlerState()}`);
				}
				alarm1Play();
			} else if (countdown === secondAlarm.timeout) {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[audio] play() alarm2 @${countdown}; ${howlerState()}`);
				}
				alarm1Stop();
				alarm2Play();
			} else if (countdown === thirdAlarm.timeout) {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[audio] play() alarm3 @${countdown}; ${howlerState()}`);
				}
				alarm2Stop();
				alarm3Play();
			}
		}
	}, [
		countdown,
		alarm1Play,
		alarm1Stop,
		alarm2Play,
		alarm2Stop,
		alarm3Play,
		displayWarningTimeout,
		firstAlarm.timeout,
		playSoundParamFromUrl,
		secondAlarm.timeout,
		thirdAlarm.timeout,
	]);

	// TEMP: surface a load/play error from any alarm on the kiosk overlay (a failed mp3 fetch or a
	// gesture-blocked <audio> element shows up here). Gated by ?networkDebug=true. Remove with the rest.
	useEffect(() => {
		if (!shouldTraceNetworkDebug()) {
			return;
		}
		if (alarm1.error) {
			networkTrace(`[audio] alarm1 ERROR: ${alarm1.error}`);
		}
		if (alarm2.error) {
			networkTrace(`[audio] alarm2 ERROR: ${alarm2.error}`);
		}
		if (alarm3.error) {
			networkTrace(`[audio] alarm3 ERROR: ${alarm3.error}`);
		}
	}, [alarm1.error, alarm2.error, alarm3.error]);

	const handleResetCountdownRef = useRef(handleResetCountdown);
	handleResetCountdownRef.current = handleResetCountdown;

	const stableResetCountdown = useCallback(() => handleResetCountdownRef.current(), []);

	return {
		resetCountdown: stableResetCountdown,
		displayOpenedDoorWarning: displayOpenedDoorWarning,
	};
};
